import { toast } from 'sonner';
import { useCallback, useRef } from 'react';

export default function useToast() {
  const activeToasts = useRef({});

  // Show a toast with an optional action button
  const show = useCallback((message, options = {}) => {
    const { id, type = 'info', action, duration = 5000, ...rest } = options;
    const toastId = id || message;

    // Prevent duplicate toasts
    if (activeToasts.current[toastId]) return;
    activeToasts.current[toastId] = true;

    const toastOptions = {
      id: toastId,
      duration,
      dismissible: true,
      onDismiss: () => { delete activeToasts.current[toastId]; },
      onAutoClose: () => { delete activeToasts.current[toastId]; },
      ...rest,
    };

    // If there's an action, render it as a button inside the toast
    if (action) {
      const actionComponent = (
        <button
          onClick={() => {
            action.onClick();
            toast.dismiss(toastId);
          }}
          style={{
            background: 'var(--accent-primary)',
            border: 'none',
            color: 'white',
            padding: '4px 12px',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-semibold)',
            whiteSpace: 'nowrap',
          }}
        >
          {action.label}
        </button>
      );
      toastOptions.action = actionComponent;
    }

    switch (type) {
      case 'success': toast.success(message, toastOptions); break;
      case 'error': toast.error(message, toastOptions); break;
      case 'warning': toast.warning(message, toastOptions); break;
      default: toast(message, toastOptions);
    }
  }, []);

  // Convenience methods
  const success = useCallback((message, options) => show(message, { ...options, type: 'success' }), [show]);
  const error = useCallback((message, options) => show(message, { ...options, type: 'error' }), [show]);
  const warning = useCallback((message, options) => show(message, { ...options, type: 'warning' }), [show]);
  const info = useCallback((message, options) => show(message, { ...options, type: 'info' }), [show]);

  return { show, success, error, warning, info, dismiss: toast.dismiss };
}